import { useMemo } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { categories } from '@/mocks/categories';
import { comics } from '@/mocks/comics';
import { getCategoryName } from '@/utils/comicHelpers';
import { useSeo } from '@/hooks/useSeo';

const sortOptions = [
  { key: 'new', label: '最新更新' },
  { key: 'hot', label: '人气最高' },
  { key: 'rating', label: '评分最高' },
];

function viewsToNumber(views: string): number {
  const match = views.match(/^([\d.]+)(亿|万)?$/);
  if (!match) {
    return 0;
  }
  const num = parseFloat(match[1]);
  if (match[2] === '亿') {
    return num * 1e8;
  }
  if (match[2] === '万') {
    return num * 1e4;
  }
  return num;
}

export default function Category() {
  const { catId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q')?.trim() || '';
  const sort = searchParams.get('sort') || 'new';

  const categoryName = getCategoryName(catId);

  const filtered = useMemo(() => {
    let list =
      catId && catId !== 'all'
        ? comics.filter((c) => c.categories.includes(catId))
        : [...comics];
    if (q) {
      list = list.filter(
        (c) =>
          c.title.includes(q) ||
          c.description.includes(q) ||
          c.author.includes(q),
      );
    }
    if (sort === 'hot') {
      list.sort((a, b) => viewsToNumber(b.views) - viewsToNumber(a.views));
    } else if (sort === 'rating') {
      list.sort((a, b) => b.rating - a.rating);
    } else {
      list.sort((a, b) => b.updateTime.localeCompare(a.updateTime));
    }
    return list;
  }, [catId, q, sort]);

  const pageTitle = q
    ? `搜索“${q}”`
    : catId && catId !== 'all'
      ? `${categoryName}漫画`
      : '全部漫画';

  const handleSort = (key: string) => {
    const params: Record<string, string> = {};
    if (q) {
      params.q = q;
    }
    if (key !== 'new') {
      params.sort = key;
    }
    setSearchParams(params);
  };

  const canonicalPath = `/category${catId && catId !== 'all' ? `/${catId}` : ''}`;

  useSeo({
    title: q
      ? `${q}漫画在线免费阅读-布丁漫画网`
      : `${pageTitle}在线免费阅读-布丁漫画网`,
    description: q
      ? `在布丁漫画网搜索“${q}”相关漫画，${q}漫画全集在线免费阅读，畅读全集中文版本，每日更新。`
      : `${pageTitle}在线免费阅读尽在布丁漫画网，收录《寄宿日记》等千余部热门${pageTitle}，全集中文版本免费畅读，每日更新。`,
    keywords: `${pageTitle},漫画,在线免费阅读,布丁漫画网,寄宿日记`,
    path: canonicalPath,
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: '首页',
            item: `${window.location.origin}${__BASE_PATH__ || ''}/`,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: pageTitle,
            item: `${window.location.origin}${__BASE_PATH__ || ''}${canonicalPath}`,
          },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        itemListElement: filtered.slice(0, 10).map((c, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: c.title,
          url: `${window.location.origin}${__BASE_PATH__ || ''}/comic/${c.id}`,
        })),
      },
    ],
  });

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-10">
      <Breadcrumb
        items={[
          { label: '首页', to: '/' },
          { label: q ? '搜索' : pageTitle },
        ]}
      />

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl text-foreground-950 md:text-2xl">{pageTitle}</h1>
        <p className="text-sm text-foreground-500">
          共找到 <strong className="font-semibold text-primary-600">{filtered.length}</strong>{' '}
          部作品，全部在线免费阅读
        </p>
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-2">
        <Link
          to="/category"
          className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
            !catId || catId === 'all'
              ? 'bg-primary-500 text-background-50'
              : 'bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-700'
          }`}
        >
          全部
        </Link>
        {categories.map((cat) => (
          <Link
            key={cat.id}
            to={`/category/${cat.id}`}
            className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
              catId === cat.id
                ? 'bg-primary-500 text-background-50'
                : 'bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-700'
            }`}
          >
            {cat.name}
          </Link>
        ))}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {sortOptions.map((opt) => (
          <button
            key={opt.key}
            type="button"
            onClick={() => handleSort(opt.key)}
            className={`flex items-center gap-1 whitespace-nowrap rounded-md px-3 py-1.5 text-sm transition-colors ${
              sort === opt.key
                ? 'bg-secondary-500 text-background-50'
                : 'bg-background-100 text-foreground-700 hover:bg-secondary-100 hover:text-secondary-900'
            }`}
          >
            {sort === opt.key && <i className="ri-check-line text-xs"></i>}
            {opt.label}
          </button>
        ))}
      </div>

      {filtered.length > 0 ? (
        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-5 xl:grid-cols-6">
          {filtered.map((comic) => (
            <ComicCard key={comic.id} comic={comic} />
          ))}
        </div>
      ) : (
        <div className="mt-10 flex flex-col items-center justify-center rounded-2xl bg-background-100 py-16 text-center">
          <i className="ri-file-search-line text-4xl text-foreground-400"></i>
          <p className="mt-3 text-sm text-foreground-600">
            没有找到与“{q || categoryName}”相关的漫画
          </p>
          <Link
            to="/category"
            className="mt-4 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2 text-sm text-background-50 transition-colors hover:bg-primary-600"
          >
            返回全部漫画
          </Link>
        </div>
      )}
    </div>
  );
}